// src/pages/DashboardPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthProvider";
import {
  FaToolbox,
  FaUsersCog,
  FaClipboardList,
  FaCalendarAlt,
  FaSyncAlt,
} from "react-icons/fa";

import api from "@/lib/axios";
import DashboardHeader from "@/components/Dashboard/DashboardHeader";
import ReportEntryCard from "@/components/Reports/ReportEntryCard";
import InventorySummary from "@/components/Dashboard/InventorySummary";
import StaffInsightsTable from "@/components/Staff/StaffInsightsTable";

import ensureArray from "@/utils/ensureArray";

const lc = (s) => String(s || "").toLowerCase();
const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

async function apiGetFirst(paths, config = {}) {
  let lastErr = null;

  for (const path of paths) {
    try {
      const response = await api.get(path, config);
      return { response, usedPath: path };
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function pickArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function pickObject(data) {
  if (isObj(data?.data)) return data.data;
  if (isObj(data?.summary)) return data.summary;
  if (isObj(data?.totals)) return data.totals;
  if (isObj(data?.kpis)) return data.kpis;
  if (isObj(data)) return data;
  return {};
}

function pickRawObject(data) {
  return isObj(data) ? data : {};
}

function safeTime(value) {
  try {
    if (!value) return "—";
    return new Date(value).toLocaleString("en-GB");
  } catch {
    return "—";
  }
}

function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function buildInventorySummary(rows) {
  const list = Array.isArray(rows) ? rows : [];

  const totalUnits = list.reduce((acc, item) => {
    const qty =
      item?.on_hand_qty != null
        ? Number(item.on_hand_qty)
        : item?.quantity != null
          ? Number(item.quantity)
          : 0;

    return acc + (Number.isFinite(qty) ? qty : 0);
  }, 0);

  return {
    rows: list,
    lineCount: list.length,
    stockLines: list.length,
    totalUnits,
  };
}


function SectionShell({ eyebrow, title, subtitle, children }) {
  return (
    <section className="rounded-[2rem] border border-slate-200/80 bg-white/95 p-5 shadow-sm ring-1 ring-white/70 md:p-7">
      <div className="mb-6">
        {eyebrow ? (
          <div className="mb-2 inline-flex rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-cyan-700">
            {eyebrow}
          </div>
        ) : null}

        <h2 className="text-2xl font-black tracking-tight text-slate-950 md:text-4xl">
          {title}
        </h2>

        {subtitle ? (
          <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 md:text-base">
            {subtitle}
          </p>
        ) : null}
      </div>

      {children}
    </section>
  );
}

function DashboardCardFrame({ children, className = "" }) {
  return (
    <div
      className={`min-w-0 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

function AccessTile({ title, description, buttonText, onClick, tone = "cyan" }) {
  const toneClass = {
    cyan: "from-cyan-500/15 via-sky-500/10 to-indigo-500/10 border-cyan-200",
    emerald: "from-emerald-500/15 via-teal-500/10 to-cyan-500/10 border-emerald-200",
    violet: "from-violet-500/15 via-indigo-500/10 to-cyan-500/10 border-violet-200",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex min-h-[260px] w-full flex-col justify-between rounded-[2rem] border bg-gradient-to-br p-7 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg ${
        toneClass[tone] || toneClass.cyan
      }`}
    >
      <div>
        <h3 className="text-3xl font-black text-slate-950">{title}</h3>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-700 md:text-base">
          {description}
        </p>
      </div>

      <span className="mt-8 inline-flex w-fit items-center rounded-2xl border border-slate-200 bg-white/85 px-5 py-3 text-sm font-black text-slate-800 transition group-hover:border-cyan-300 group-hover:text-cyan-700">
        {buttonText} →
      </span>
    </button>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { role, user } = useAuth();

  const roles = ensureArray(user?.roles ?? role).map(lc);

  const allow = {
    procurement: ["admin", "developer", "procurement"],
    inventory: ["admin", "developer", "procurement", "finance"],
    staffing: ["admin", "developer", "manager"],
    maintenance: ["admin", "developer", "manager"],
    eventPlanning: ["admin", "developer", "manager"],
    timesheets: ["admin", "developer", "finance", "manager"],
    finance: ["admin", "finance", "developer"],
    revenue: ["admin", "finance", "developer"],
    executive: ["admin", "finance", "developer"],
  };

  const can = (bucket) => {
    const list = ensureArray(allow[bucket]).map(lc);
    if (!list.length) return false;
    return roles.some((r) => list.includes(r));
  };

  const allModules = [
    { key: "procurement", icon: <FaClipboardList />, label: "Procurement", route: "/procurement" },
    { key: "staffing", icon: <FaUsersCog />, label: "Staffing", route: "/staffing-dashboard" },
    { key: "maintenance", icon: <FaToolbox />, label: "Maintenance", route: "/maintenance" },
    { key: "eventPlanning", icon: <FaCalendarAlt />, label: "Events", route: "/events" },
  ];

  const sidebarModules = ensureArray(allModules).filter((m) => can(m.key));

  const [dashboardData, setDashboardData] = useState({
    procurement: {},
    inventory: {},
    staffingSummary: {},
  });

  const [dashboardSources, setDashboardSources] = useState({
    procurement: null,
    inventory: null,
    staffingSummary: null,
  });

  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);
  const [dashboardError, setDashboardError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const reqSeq = useRef(0);

  const loadDashboardData = useCallback(async ({ silent = false } = {}) => {
    const seq = ++reqSeq.current;

    if (silent) setDashboardRefreshing(true);
    else setDashboardLoading(true);

    setDashboardError("");

    const safeFetchObject = async (paths) => {
      try {
        const { response, usedPath } = await apiGetFirst(paths);
        return { ok: true, value: pickObject(response.data), raw: response.data, source: usedPath };
      } catch (err) {
        return { ok: false, value: {}, raw: null, source: null, error: err };
      }
    };

    const safeFetchArray = async (paths) => {
      try {
        const { response, usedPath } = await apiGetFirst(paths);
        return { ok: true, value: pickArray(response.data), raw: response.data, source: usedPath };
      } catch (err) {
        return { ok: false, value: [], raw: null, source: null, error: err };
      }
    };

    try {
      const [
        procurementResp,
        inventoryResp,
        staffingSummaryResp,
      ] = await Promise.all([
        safeFetchObject([
          "/solo-mdsh-api/reports/procurement/kpis",
          "/solo-mdsh-api/analytics/procurement/summary",
        ]),
        safeFetchArray(["/solo-mdsh-api/inventory/stock-levels"]),
        safeFetchObject(["/solo-mdsh-api/staffing/summary"]),
      ]);

      if (reqSeq.current !== seq) return;

      const inventorySummary = buildInventorySummary(inventoryResp.value);

      setDashboardData({
        procurement: procurementResp.value || {},
        inventory: inventorySummary,
        staffingSummary: staffingSummaryResp.value || {},
      });

      setDashboardSources({
        procurement: procurementResp.source,
        inventory: inventoryResp.source,
        staffingSummary: staffingSummaryResp.source,
      });

      setLastUpdated(new Date().toISOString());

      const allFailed =
        !procurementResp.ok &&
        !inventoryResp.ok &&
        !staffingSummaryResp.ok;

      if (allFailed) {
        setDashboardError("Live dashboard data could not be loaded from the current endpoints.");
      }
    } catch (err) {
      if (reqSeq.current !== seq) return;

      console.error("Failed to load dashboard data:", err);

      setDashboardError(
        err?.response?.data?.error || err?.message || "Failed to load dashboard data."
      );
    } finally {
      if (reqSeq.current !== seq) return;
      setDashboardLoading(false);
      setDashboardRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData({ silent: false });
  }, [loadDashboardData]);

  const summaryBadgeText = useMemo(() => {
    if (dashboardLoading) return "Loading live dashboard data…";
    if (dashboardRefreshing) return "Refreshing live dashboard data…";
    if (dashboardError) return dashboardError;
    return `Live dashboard data connected • Updated ${safeTime(lastUpdated)}`;
  }, [dashboardLoading, dashboardRefreshing, dashboardError, lastUpdated]);


  return (
    <div className="flex min-h-screen bg-[radial-gradient(circle_at_top_left,#ecfeff_0,#f8fafc_38%,#ffffff_100%)] text-slate-900">
      <aside className="hidden w-24 shrink-0 flex-col items-center border-r border-slate-200 bg-white/90 py-8 shadow-sm lg:flex">
        <div className="mb-2 text-[11px] font-black uppercase tracking-[0.25em] text-cyan-600">
          SOLO
        </div>
        <div className="mb-6 text-[10px] font-semibold text-slate-400">Dashboard</div>

        <nav aria-label="Primary" className="flex flex-col items-center gap-3">
          {sidebarModules.map((mod) => (
            <button
              key={mod.key}
              onClick={() => navigate(mod.route)}
              className="group flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700"
              title={mod.label}
              aria-label={mod.label}
              type="button"
            >
              <span className="text-xl">{mod.icon}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 px-4 py-6 md:px-8 md:py-8 xl:px-10">
        <div className="mx-auto max-w-[1500px]">
          <DashboardHeader user={user} />

          <section className="mt-6 rounded-[1.75rem] border border-slate-200 bg-white/90 p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div
                className={`text-sm font-medium ${
                  dashboardError
                    ? "text-rose-700"
                    : dashboardLoading || dashboardRefreshing
                      ? "text-sky-700"
                      : "text-slate-600"
                }`}
              >
                {summaryBadgeText}
              </div>

              <button
                onClick={() => loadDashboardData({ silent: true })}
                disabled={dashboardLoading || dashboardRefreshing}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-950 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-cyan-700 disabled:opacity-60"
                type="button"
              >
                <FaSyncAlt className={dashboardRefreshing ? "animate-spin" : ""} />
                Refresh dashboard
              </button>
            </div>
          </section>

          <section className="mt-6">
            <ReportEntryCard />
          </section>

          <div className="mt-8 space-y-8">
            {can("timesheets") && (
              <SectionShell
                eyebrow="People operations"
                title="Timesheets"
                subtitle="Open timesheet approvals and workforce records."
              >
                <div className="space-y-6">
                  {can("timesheets") && (
                    <AccessTile
                      title="Smart Timesheet"
                      description="Open approvals, labour signals, clock data, payroll preparation, and staffing cost control in one focused operational view."
                      buttonText="Open timesheets"
                      onClick={() => navigate("/shift-planner")}
                      tone="cyan"
                    />
                  )}
                </div>
              </SectionShell>
            )}

            {(can("procurement") || can("maintenance") || can("inventory")) && (
              <SectionShell
                eyebrow="Operations command"
                title="Operations Health"
                subtitle="Large operational cards for procurement, maintenance, and inventory without compressed columns."
              >
                <div className="space-y-6">
                  {can("procurement") && (
                    <AccessTile title="Procurement" description="Review purchase orders, approval queues, receiving and currency-aware procurement totals."
                      buttonText="Open procurement" onClick={() => navigate("/procurement")} tone="cyan" />
                  )}

                  {can("maintenance") && (
                    <AccessTile title="Maintenance" description="Open task activity and maintenance records. Period totals are available in Business reports."
                      buttonText="Open maintenance" onClick={() => navigate("/maintenance")} tone="cyan" />
                  )}

                  {can("inventory") && (
                    <DashboardCardFrame>
                      <InventorySummary
                        onClick={() => navigate("/inventory")}
                        data={dashboardData.inventory}
                        loading={dashboardLoading}
                        error={dashboardError}
                        source={dashboardSources.inventory}
                      />
                    </DashboardCardFrame>
                  )}
                </div>
              </SectionShell>
            )}

            {can("staffing") && (
              <SectionShell
                eyebrow="People command"
                title="Staffing & Workforce"
                subtitle="People visibility, staffing insight, and workforce access in wider dashboard cards."
              >
                <div className="space-y-6">
                  <DashboardCardFrame>
                    <StaffInsightsTable
                      onClick={() => navigate("/staffing-dashboard")}
                      data={dashboardData.staffingSummary}
                      loading={dashboardLoading}
                      error=""
                      source={dashboardSources.staffingSummary}
                    />
                  </DashboardCardFrame>

                  <AccessTile
                    title="Workforce View"
                    description="Open the staffing dashboard for labour coverage, risk signals, payroll preparation, and team-level visibility."
                    buttonText="Open staffing"
                    onClick={() => navigate("/staffing-dashboard")}
                    tone="violet"
                  />
                </div>
              </SectionShell>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}
